import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Checkbox, FormGroup, Input, Select, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import FormField from "components/cck/FormField";
import AgreementTemplateSelect, { AgreementTemplateSelectOption } from "components/selects/AgreementTemplateSelect";

import { gql } from "__generated__/gql";
import { Agreement } from "types/index";
import { CharterSubtype, CharterType } from "__generated__/enums";

type AgreementCreateTemplateDialogProps = {
	open: boolean;
	onClose: () => void;
	compatibleCharterType: CharterType;
	compatibleCharterSubtype: CharterSubtype;
	agreements: Pick<Agreement, "id" | "type" | "description" | "summary">[];
};

type TemplateForm = {
	description: string | undefined;
	agreementIds: number[];
	overrideTemplateId?: number;
	isDefault?: boolean;
};

const AgreementCreateTemplateDialog = ({ open, onClose, compatibleCharterType, compatibleCharterSubtype, agreements }: AgreementCreateTemplateDialogProps) => {

	const [ selectedTemplate, setSelectedTemplate ] = useState<AgreementTemplateSelectOption | null>(null);

	const agreementOptions = agreements.map(x => ({
		id: x.id,
		type: x.type,
		description: x.description,
		summary: x.summary,
	}));

	const defaultValues: TemplateForm = {
		description: "",
		overrideTemplateId: undefined,
		agreementIds: agreements?.map(x => x.id) ?? [],
		isDefault: false,
	};

	const { control, handleSubmit, formState, reset, setValue } = useForm<TemplateForm>({
		defaultValues: defaultValues,
	});

	const [ saveAgreementTemplate, { loading } ] = useMutation(SAVE_AGREEMENT_TEMPLATE_MUTATION);

	const handleOnClose = () => {
		onClose();
		setSelectedTemplate(null);
		reset(defaultValues);
	};

	const save = (data: TemplateForm) => {
		saveAgreementTemplate({
			variables: {
				input: {
					id: data.overrideTemplateId ?? undefined,
					agreements: data.agreementIds,
					description: data.description || "",
					compatibleCharterType: compatibleCharterType,
					compatibleCharterSubtype: compatibleCharterSubtype,
					isDefault: data.isDefault,

				},
			},
		}).then(() => {
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
			handleOnClose();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CREATE_TEMPLATE" })}
			form='agreementCreateTemplateForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="agreementCreateTemplateForm">
				<FormGroup label={intl.formatMessage({ id: "DESCRIPTION" })} overflow="hidden" required>
					<FormField name="description" errors={formState.errors}>
						<Controller
							name='description'
							control={control}
							rules={{ required: true }}
							render={({ field }) => <Input {...field} autoFocus />} />
					</FormField>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "AGREEMENTS" })} overflow="hidden" required>
					<FormField name="agreementIds" errors={formState.errors}>
						<Controller
							name='agreementIds'
							control={control}
							rules={{ required: true }}
							render={({ field }) => {
								const selectedTemplates = new Set<number>(field.value ?? []);
								const values = agreementOptions.filter(x => selectedTemplates.has(x.id));

								return (
									<Select
										{...field}
										value={values}
										onChange={(val) => field.onChange(val?.map(x => x.id))}
										fetchOptions={agreementOptions}
										getOptionLabel={(option) => option.description || intl.formatMessage({ id: `AGREEMENT_TYPE_${option.type}` }) || ""}
										renderOption={(props, option) => (
											<li {...props}>
												<div>
													<div>{option.description || intl.formatMessage({ id: `AGREEMENT_TYPE_${option.type}` })}</div>
													{option.summary && (
														<div>
															<small>{option.summary}</small>
														</div>
													)}
												</div>
											</li>
										)}
										multiple />
								);
							}} />
					</FormField>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "OVERWRITE_TEMPLATE" })} overflow="hidden" tooltip={intl.formatMessage({ id: "AGREEMENT_TEMPLATE_COMPATIBILITY" })}>
					<AgreementTemplateSelect
						value={selectedTemplate}
						onChange={(val) => {
							setSelectedTemplate(val ?? null);
							setValue("overrideTemplateId", val?.id ?? undefined);
						}}
						charterType={compatibleCharterType}
						charterSubtype={compatibleCharterSubtype}
					/>
				</FormGroup>
				<FormGroup label={intl.formatMessage({ id: "DEFAULT" })} >
					<FormField name="isDefault" errors={formState.errors}>
						<Controller
							name='isDefault'
							control={control}
							rules={{ required: false }}
							render={({ field }) =>  <Checkbox checked={field.value} onChange={event => field.onChange(event.target.checked)} />} />
					</FormField>
				</FormGroup>
			</form>
		</FormDialog>
	);
};

const SAVE_AGREEMENT_TEMPLATE_MUTATION = gql(`#graphql
	mutation save_agreement_template($input: AgreementTemplateInput!) {
		saveAgreementTemplate(input: $input)
	}
`);

export default AgreementCreateTemplateDialog;
